import { initDropdowns } from "./signet-dropdowns.js";
import { api, uploadFiles } from "./signet-api.js";
import { initServiceNavigation } from "./signet-service-nav.js";
import { initContact } from "./signet-contact.js";
import { recommend, directionReadiness } from "./signet-audit.js?v=20261003-refined";
const config = JSON.parse(
  document.getElementById("signet-config")?.textContent || "{}",
);
export function track(name, detail = {}) {
  document.dispatchEvent(
    new CustomEvent("signet:analytics", { detail: { name, ...detail } }),
  );
}
function element(tag, text, className) {
  const el = document.createElement(tag);
  if (text) el.textContent = text;
  if (className) el.className = className;
  return el;
}
function setup(root = document) {
  initContact(root);
  initServiceNavigation();
  const header = root.querySelector("[data-sf-header]");
  if (header && !header.dataset.ready) {
    header.dataset.ready = "true";
    const dialog = header.querySelector("dialog");
    const toggle = header.querySelector(".sf-menu-toggle");
    toggle.addEventListener("click", () => {
      dialog.showModal();
      toggle.setAttribute("aria-expanded", "true");
    });
    header
      .querySelector(".sf-menu-close")
      .addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => {
      toggle.setAttribute("aria-expanded", "false");
      toggle.focus();
    });
    addEventListener(
      "scroll",
      () => header.classList.toggle("is-scrolled", scrollY > 28),
      { passive: true },
    );
  }
  root.querySelectorAll("[data-sf-filter]").forEach((button) =>
    button.addEventListener("click", () => {
      const section = button.closest("section");
      let count = 0;
      section
        .querySelectorAll("[data-sf-filter]")
        .forEach((b) => b.setAttribute("aria-pressed", String(b === button)));
      section.querySelectorAll("[data-category]").forEach((card) => {
        card.hidden =
          button.dataset.sfFilter !== "All" &&
          card.dataset.category !== button.dataset.sfFilter;
        if (!card.hidden) count++;
      });
      section.querySelector(".sf-filter-empty").hidden =
        count > 0 || button.dataset.sfFilter === "All";
    }),
  );
  root.querySelectorAll("[data-sf-form]").forEach(initForm);
}
function data(form) {
  const result = {};
  const fd = new FormData(form);
  for (const key of new Set(fd.keys())) {
    const values = fd.getAll(key).filter((v) => !(v instanceof File));
    result[key] =
      values.length > 1 ||
      ["platforms", "materials", "specialties", "tools"].includes(key)
        ? values
        : values[0] || "";
  }
  return result;
}
function initForm(form) {
  if (form.dataset.ready) return;
  form.dataset.ready = "true";
  const kind = form.dataset.sfForm;
  const steps = [...form.querySelectorAll("[data-step]")];
  let step = 0;
  let busy = false;
  let resultData;
  const error = form.querySelector("[data-form-error]");
  const next = form.querySelector("[data-next]");
  const back = form.querySelector("[data-back]");
  const submit = form.querySelector("[data-submit]");
  const status = form.querySelector("[data-form-status]");
  const output = form.parentElement.querySelector("[data-form-result]");
  function show(focus = false) {
    steps.forEach((s, i) => (s.hidden = i !== step));
    back.hidden = step === 0;
    next.hidden = step === steps.length - 1;
    submit.hidden = step !== steps.length - 1;
    const label = form.querySelector("[data-progress-label]");
    if (label) label.textContent = `Step ${step + 1} of ${steps.length}`;
    form.querySelectorAll("[data-rail-step]").forEach((item, i) => {
      if (i === step) item.setAttribute("aria-current", "step");
      else item.removeAttribute("aria-current");
      item.classList.toggle("is-complete", i < step);
    });
    const progress = form.querySelector("progress");
    if (progress) progress.value = step + 1;
    error.textContent = "";
    if (focus) {
      steps[step].querySelector("input,select,textarea")?.focus();
      track("form_step", { form: kind, step: step + 1 });
    }
  }
  function validate(fieldset) {
    const invalid = [
      ...fieldset.querySelectorAll("input,select,textarea"),
    ].find((el) => !el.disabled && !el.checkValidity());
    if (invalid) {
      error.textContent =
        invalid.name === "email"
          ? "Please add a work email."
          : `Please check ${invalid.labels?.[0]?.textContent.replace("(optional)", "").trim() || "this field"}.`;
      invalid.setAttribute("aria-invalid", "true");
      if (!error.id) error.id = `${kind}-error`;
      invalid.setAttribute("aria-describedby", error.id);
      invalid.focus();
      return false;
    }
    return true;
  }
  show();
  next.addEventListener("click", () => {
    if (validate(steps[step])) {
      step++;
      show(true);
    }
  });
  back.addEventListener("click", () => {
    step--;
    show(true);
  });
  form.addEventListener("input", (event) =>
    event.target.removeAttribute("aria-invalid"),
  );
  const flexible = form.elements.flexible;
  flexible?.addEventListener("change", () => {
    form.elements.deliveryDate.disabled = flexible.checked;
  });
  const direction = form.elements.direction;
  if (kind === "project") {
    const save = form.querySelector("[data-save-draft]");
    const key = "signet-project-draft-v1";
    const clear = () => {
      try {
        localStorage.removeItem(key);
      } catch {}
      save.checked = false;
      status.textContent = "Saved progress cleared.";
    };
    try {
      const draft = JSON.parse(localStorage.getItem(key) || "null");
      if (draft && Date.now() - draft.saved < 7 * 86400000) {
        for (const field of form.elements) {
          if (!field.name || field.type === "file") continue;
          const value = draft.fields[field.name];
          if (field.type === "checkbox")
            field.checked = Array.isArray(value)
              ? value.includes(field.value)
              : Boolean(value);
          else if (value !== undefined) field.value = value;
        }
        save.checked = true;
        status.textContent = "Your saved progress has been restored.";
        if (flexible) form.elements.deliveryDate.disabled = flexible.checked;
      }
    } catch {}
    form.addEventListener("input", () => {
      if (!save.checked) return;
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ saved: Date.now(), fields: data(form) }),
        );
      } catch {
        status.textContent = "This browser could not save progress.";
      }
    });
    save.addEventListener("change", () => {
      if (!save.checked) clear();
    });
    form.querySelector("[data-clear-draft]").addEventListener("click", clear);
    const selected = new URLSearchParams(location.search).get("service");
    if (selected)
      fetch(config.servicesUrl)
        .then((r) => r.json())
        .then((services) => {
          const service = services.find((s) => s.slug === selected);
          if (service) form.elements.service.value = service.heading;
        })
        .catch(() => {});
  }
  const drop = form.querySelector("[data-dropzone]");
  if (drop) {
    const input = drop.querySelector("input");
    const list = drop.querySelector("[data-file-list]");
    const showFiles = () => {
      list.textContent = [...input.files].map((f) => f.name).join(", ");
    };
    input.addEventListener("change", showFiles);
    ["dragenter", "dragover"].forEach((name) =>
      drop.addEventListener(name, (e) => {
        e.preventDefault();
        drop.classList.add("is-dragging");
      }),
    );
    drop.addEventListener("dragleave", () =>
      drop.classList.remove("is-dragging"),
    );
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      drop.classList.remove("is-dragging");
      input.files = e.dataTransfer.files;
      showFiles();
    });
  }
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (busy) return;
    if (step < steps.length - 1) {
      next.click();
      return;
    }
    for (let i = 0; i < steps.length; i++) {
      if (!validate(steps[i])) {
        step = i;
        show();
        validate(steps[i]);
        return;
      }
    }
    const payload = data(form);
    if (payload.company_fax) return;
    delete payload.company_fax;
    busy = true;
    submit.disabled = true;
    error.textContent = "";
    status.textContent =
      kind === "audit" ? "Preparing your recommendation…" : "Submitting…";
    try {
      if (kind === "audit") {
        resultData = { answers: payload, recommendation: recommend(payload) };
        output.replaceChildren(
          element("p", "YOUR VIDEO SYSTEM AUDIT", "sf-eyebrow"),
          element("h2", resultData.recommendation.title),
          element("p", resultData.recommendation.reason),
        );
        const link = element("a", "Start a Project", "sf-button");
        link.href =
          "/pages/start-a-project" +
          (resultData.recommendation.service
            ? "?service=" + resultData.recommendation.service
            : "");
        output.append(link);
        const summary=element('div',undefined,'sf-audit-summary');
        const score=directionReadiness(payload);
        summary.append(element('p','Creative direction readiness','sf-eyebrow'),element('strong',score.label),element('p',score.explanation));
        const meter=element('progress');meter.max=3;meter.value=score.value;meter.setAttribute('aria-label','Creative direction readiness: '+score.label);summary.append(meter);
        summary.append(element('small','A self-assessment based on your answers.'));
        output.prepend(summary);
        const complete=element('p','7 of 7 answers reviewed','sf-audit-complete');
        const progress=element('progress');progress.max=7;progress.value=7;progress.setAttribute('aria-label','Audit complete');
        output.prepend(complete,progress);
        const details=element('a',resultData.recommendation.service?'Explore This Service':'Schedule a Conversation','sf-button sf-secondary');
        details.href=resultData.recommendation.service?'/pages/'+resultData.recommendation.service:'/pages/contact#schedule-a-call';output.append(details);
        output.hidden = false;
        output.focus();
        status.textContent = "Your recommendation is ready below.";
        track("audit_completed", { route: resultData.recommendation.service });
      } else {
        const files = [...(form.elements.uploads?.files || [])];
        payload.fileIds = files.length ? await uploadFiles(files) : [];
        const response = await api(
          kind === "project" ? "/leads" : kind === "contact" ? "/contact" : kind === "call" ? "/call-requests" : "/editor-applications",
          { method: "POST", body: payload },
        );
        if (!response.id)
          throw new Error(
            "Your submission was not confirmed. Please try again.",
          );
        const project = kind === "project";
        output.replaceChildren(
          element(
            "h2",
            project ? "Your project is in review." : kind === "contact" ? "Message received." : kind === "call" ? "Call request received." : "Application received.",
          ),
          element(
            "p",
            project
              ? "Got it. We will review your brief and reply with the clearest next step - either a direct editing quote, a short scoping conversation, or a recommendation to start with the Video System Audit."
              : kind === "contact" ? "Thanks for reaching out. We will review your message and reply to the email address you provided." : kind === "call" ? "Thanks for making time. We’ll review your preferred date and time and reply by email to confirm the call." : "Thanks for applying to the Signet Frames editor network. We'll review your application and contact you by email if there's a fit.",
          ),
          element(
            "p",
            project
              ? "We will look at the scope, footage, timing and level of direction you need. If the brief is already clear, we can reply with the next execution step. If the direction still needs work, we will tell you that before adding unnecessary scope."
              : kind === "contact" ? "Your inquiry has been saved for our team." : kind === "call" ? "Your request has been saved. The call is not booked until our team confirms it." : "Submitting an application does not automatically provide access to the Signet Frames editor portal.",
          ),
        );
        const link = element(
          "a",
          project ? "Back to Home" : "Back to Signet Frames",
          "sf-button",
        );
        link.href = "/";
        output.append(link);
        form.hidden = true;
        output.hidden = false;
        output.focus();
        if (project) {
          try {
            localStorage.removeItem("signet-project-draft-v1");
          } catch {}
        }
        track("form_completed", { form: kind });
      }
    } catch (e) {
      error.textContent = e.message;
      error.focus();
      status.textContent =
        "Nothing has been confirmed. Your entries are still available.";
    } finally {
      busy = false;
      submit.disabled = false;
    }
  });
}
setup();
initDropdowns();
document.addEventListener("shopify:section:load", (event) =>
  setup(event.target),
);
document.addEventListener("click", (event) => {
  const a = event.target.closest("a");
  if (a?.getAttribute("href")?.includes("/start-a-project"))
    track("project_cta", { path: location.pathname });
});
if (document.querySelector("[data-sf-portal]")) import("./signet-portal.js?v=20261004-portal-mail37");
